import { memo, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Grid2, Box, CircularProgress, Typography } from '@mui/material';
import { DataGridPro, useGridApiRef, type GridSortModel, type GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { GridCellParams } from '@mui/x-data-grid-pro';
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { useCommonStyles } from '../../styles/GenericStyle';
import { getDataGridStyles } from '../../styles/MtComStyleObj';
import { useResponsiveHeaderHeight } from '../../hooks/useResponsiveHeaderHeight';
import { greyHeader } from "../../colors";
import { GenericConstant } from "../../utils/GenericConstant";
import { getSPLotGridCols } from '../../utils/SPLotGridConfig';
import { addIdIncrementWithAddCrop, checkArray } from '../../utils/HelperFunction';
import { useExportToExcel } from '../../utils/ExcelExport';
import PrintSPLotLabelDialog from '../common/PrintSPLotLabelDialog';
import { FloatingActionBar } from '../../components/spLot/FloatingBar';
import { useGetArchivedSPLots, usegetSPLots, getDrillDownSPLotById, useSearchFilterData } from '../../api/vegApl';
import { useGlobalState } from "../../contexts/GlobalState";
import { useSnackbar } from '../../contexts/SnackbarContext';
import { buildSPLotFilterRequest, reApplyFilterWithSite } from './spLotService';
import { usePrintLabel } from '../../hooks/usePrintLabel';
import type { LabelTemplate } from '../../api/vegApl/labelTemplate';
import { usePersistedGridState } from '../../hooks/usePersistedGridState';
import { getSplotStatusIdsByTab } from '../../utils/statusUtils';

export type SPLotDialogprops = {
    hidden: boolean;
    setMode: (mode: string) => void;
    setDrillDownParam: (params: any) => void;
    gridData: String[];
    refreshSPLotGrid: boolean;
    showArchivedSPLots?: boolean;
    setRefreshSPLotGrid: (params: boolean) => void;
    selectedFilter?: string;
    splotTemplates?: LabelTemplate[];
    activeFilter?: any;
}

function SPLotDataGrid({
    hidden, setMode, setDrillDownParam, gridData,
    refreshSPLotGrid = false, showArchivedSPLots = false,
    setRefreshSPLotGrid, selectedFilter = 'Processing',
    splotTemplates: splotTemplatesProp = [],
    activeFilter = null,
}: SPLotDialogprops) {
    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(48);
    const { showSnackbar } = useSnackbar();
    const { exportToExcel } = useExportToExcel();
    const { state: { siteSelected } = {}, crops, splotStatuses } = useGlobalState();
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange } = usePersistedGridState('splot-main-grid');
    const isFetchingRef = useRef(false);
    const prevSiteRef = useRef(siteSelected);
    const prevFilterRef = useRef(selectedFilter);

    const [isLoading, setIsLoading] = useState(false);
    const [rawSplotRows, setRawSplotRows] = useState<any[]>([]);
    const [cropsMap, setCropsMap] = useState<any>({});
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'updated_date', sort: 'desc' }]);
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const isInitialMount = useRef(true);

    const {
        printLabelOpen, printLabelRow, hasTemplate, foundTemplate, isPrinting,
        openPrintDialog, closePrintDialog, bulkPrint,
    } = usePrintLabel({ templateType: 'Splot', defaultSite: siteSelected || 'MAAG' });

    const normalizeSplotRows = useCallback((source: any): any[] => {
        const payload = source?.data ?? source;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.splot_data)) return payload.splot_data;
        if (Array.isArray(payload?.splots)) return payload.splots;
        if (Array.isArray(payload?.rows)) return payload.rows;
        if (Array.isArray(payload?.records)) return payload.records;
        return [];
    }, []);

    const isSplotRow = useCallback((row: any) => {
        return !!row && typeof row === 'object' && !!row.splot_id;
    }, []);

    const spLotGridData = useMemo(
        () => addIdIncrementWithAddCrop(rawSplotRows, cropsMap),
        [rawSplotRows, cropsMap]
    );

    // --- Mutations ---

    const spLotMutationSuccess = useCallback((data: any) => {
        const rows = normalizeSplotRows(data).filter(isSplotRow);
        setRawSplotRows(rows);
        setIsLoading(false);
        isFetchingRef.current = false;
    }, [normalizeSplotRows, isSplotRow]);

    const spLotMutationError = useCallback(() => {
        setRawSplotRows([]);
        setIsLoading(false);
        isFetchingRef.current = false;
    }, []);

    const mutationOpts = { mutation: { onSuccess: spLotMutationSuccess, onError: spLotMutationError } };
    const { mutate: getAllSPLots } = usegetSPLots(mutationOpts);
    const { mutate: getAllArchivedSPLots } = useGetArchivedSPLots(mutationOpts);
    const { mutate: searchFilterSPLots } = useSearchFilterData(mutationOpts);

    // --- Handlers ---

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);

    const setDrilDownData = useCallback(async (params: any) => {
        setMode("drill");
        setDrillDownParam(params?.row ?? {});
    }, [setMode, setDrillDownParam]);

    const handleRowDrilldownClick = useCallback((params: GridCellParams<any>, event: any) => {
        if (showArchivedSPLots) return;

        const field = String(params?.field ?? '');
        if (field === '__check__' || field === 'Action') return;

        const selection = window.getSelection?.()?.toString()?.trim();
        if (selection) return;

        const target = event?.target as HTMLElement | null;
        if (target?.closest('a, button, [role="button"], .MuiDataGrid-actionsCell')) return;

        setDrilDownData(params);
    }, [showArchivedSPLots, setDrilDownData]);

    const handlePrintLabel = useCallback((params: any) => {
        const row = params?.row;
        openPrintDialog(row, row?.site_code || siteSelected || 'MAAG', row?.crop ?? '');
    }, [openPrintDialog, siteSelected]);

    const downloadCurRow = useCallback(async (params: any) => {
        try {
            const splotId = params?.row?.splot_id;
            const response = await getDrillDownSPLotById(splotId);
            const materials = (response?.data as any)?.materials ?? [];
            if (materials.length === 0) return;
            exportToExcel(materials, `SPLotMaterials_${splotId}`, ['serialNo', 'splot_workflow_protocol_id']);
        } catch (error: any) {
            showSnackbar({ message: error?.response?.data?.detail, severity: 'error' });
        }
    }, [exportToExcel, showSnackbar]);

    const closeFloater = useCallback(() => {
        setSelectionModel([]);
        if (typeof apiRef.current?.setRowSelectionModel === 'function') {
            apiRef.current.setRowSelectionModel([]);
        }
    }, [apiRef]);

    const handleBulkPrint = useCallback(async () => {
        const selectedRows = selectionModel
            .map((serialNo) => (spLotGridData as any[]).find((r: any) => r.serialNo === serialNo))
            .filter(Boolean);

        if (selectedRows.length === 0) return;
        await bulkPrint(selectedRows, siteSelected);
    }, [selectionModel, spLotGridData, bulkPrint, siteSelected]);

    // --- Effects ---

    useEffect(() => {
        const map = crops.reduce((acc: any, item: any) => {
            acc[item.crop_xrd_oid] = item.name;
            return acc;
        }, {});
        setCropsMap(map);
    }, [crops]);

    useEffect(() => {
        const rows = normalizeSplotRows(gridData);
        if (rows.length > 0 && !rows.some(isSplotRow)) {
            return;
        }
        setRawSplotRows(rows.filter(isSplotRow));
    }, [gridData, normalizeSplotRows, isSplotRow]);

    useEffect(() => {
        if (!siteSelected) return;
        const normalizedSelectedFilter = String(selectedFilter ?? '').trim();
        const effectiveSelectedFilter = normalizedSelectedFilter || 'All SPLots';
        const siteChanged = prevSiteRef.current !== siteSelected;
        const needsStatusFilter = !showArchivedSPLots && !activeFilter && effectiveSelectedFilter !== 'All SPLots' && effectiveSelectedFilter !== 'High priority MatIDs';
        const statusIds = needsStatusFilter ? getSplotStatusIdsByTab(effectiveSelectedFilter, splotStatuses) : [];

        // Signal-driven only: GenericFilterPanel (or other parent flows) drive fetches
        // by setting refreshSPLotGrid=true. This eliminates duplicate API calls that
        // previously came from mount/site/filter triggers running in parallel with
        // the panel's own onRefreshData callback.
        if (!refreshSPLotGrid && !siteChanged) return;

        // On browser refresh, statuses can arrive after the initial grid refresh signal.
        // Wait so the default Processing tab doesn't call filter_splot with status_id: [].
        if (needsStatusFilter && statusIds.length === 0) return;

        // Consume the refresh signal exactly once (when present).
        if (refreshSPLotGrid) {
            setRefreshSPLotGrid(false);
        }
        isInitialMount.current = false;
        prevSiteRef.current = siteSelected;
        prevFilterRef.current = effectiveSelectedFilter;

        // Reset any stale fetch lock before issuing a new request.
        isFetchingRef.current = false;

        if (siteChanged) {
            setRawSplotRows([]);
            closeFloater();
        }

        // ARCHIVED MODE — archived endpoint regardless of filter state.
        if (showArchivedSPLots) {
            isFetchingRef.current = true;
            setIsLoading(true);
            getAllArchivedSPLots({ siteCode: siteSelected });
            return;
        }

        // Active drawer-applied filter — re-apply with current site/archived state.
        if (activeFilter) {
            isFetchingRef.current = true;
            setIsLoading(true);
            searchFilterSPLots(reApplyFilterWithSite(activeFilter, siteSelected, showArchivedSPLots));
            return;
        }

        // Tab default: All SPLots (no filter) vs status-filtered tabs.
        isFetchingRef.current = true;
        setIsLoading(true);
        if (effectiveSelectedFilter === 'All SPLots') {
            getAllSPLots({ siteCode: siteSelected });
        } else {
            searchFilterSPLots(buildSPLotFilterRequest(effectiveSelectedFilter, showArchivedSPLots, siteSelected, splotStatuses));
        }
    }, [refreshSPLotGrid, siteSelected, showArchivedSPLots, activeFilter, selectedFilter, splotStatuses, closeFloater]);

    useEffect(() => {
        const messageHandler = (event: MessageEvent) => {
            if (event.data?.type === "PRINT_DONE") closeFloater();
        };
        window.addEventListener("message", messageHandler);
        return () => window.removeEventListener("message", messageHandler);
    }, [closeFloater]);

    // --- Render ---

    const NoDataMsg = () => (
        <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">No SPLots found</Typography>
        </Box>
    );

    return (
        !hidden && (
            <Grid2 size={{ xs: 12, sm: 12, md: 12 }} sx={{ mt: 1 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', placeItems: 'center', justifyContent: 'center', height: '100%', p: 5 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', minHeight: "100px" }}>
                        <DataGridPro
                            apiRef={apiRef}
                            columns={applyColumnWidths(getSPLotGridCols(setDrilDownData, classes, downloadCurRow, sortModel, handlePrintLabel, showArchivedSPLots))}
                            rows={checkArray(spLotGridData)}
                            getRowId={(row) => row?.serialNo}
                            getRowHeight={() => "auto"}
                            columnHeaderHeight={headerHeight}
                            pagination
                            pageSizeOptions={GenericConstant.SPLOTTPAGINATION}
                            initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            onColumnWidthChange={onColumnWidthChange}
                            onCellClick={handleRowDrilldownClick}
                            disableRowSelectionOnClick
                            disableColumnSelector={showArchivedSPLots}
                            disableColumnMenu={showArchivedSPLots}
                            checkboxSelection
                            onRowSelectionModelChange={(newModel) => setSelectionModel(newModel)}
                            slots={{ noRowsOverlay: NoDataMsg }}
                            slotProps={{
                                baseCheckbox: {
                                    checkedIcon: <Box component="img" alt="" src="icons/CheckBox.svg" />,
                                    icon: <CheckBoxOutlineBlankRoundedIcon fontSize="small" />,
                                    sx: {
                                        "&.Mui-checked": { color: '#000000' },
                                        color: '#000000',
                                        fontSize: 20,
                                        "& svg": { fontSize: 20 },
                                    },
                                },
                            }}
                            sx={(theme) => ({
                                ...getDataGridStyles(theme, {
                                    headerColor: greyHeader,
                                    fillerColor: greyHeader,
                                    maxHeight: 'calc(100vh - 340px)',
                                    minHeight: '100px',
                                }),
                                ...(showArchivedSPLots && {
                                    opacity: 0.85, pointerEvents: 'auto',
                                    '& .MuiDataGrid-row': { cursor: 'default' },
                                }),
                            })}
                        />
                    </Box>
                )}

                <PrintSPLotLabelDialog
                    open={printLabelOpen}
                    onClose={closePrintDialog}
                    onPrint={(templateName) => {
                        console.log('Print label with template:', templateName, printLabelRow);
                        closePrintDialog();
                    }}
                    onAddNewTemplate={() => console.log('Add new template')}
                    rowData={printLabelRow ?? {}}
                    hasTemplate={hasTemplate}
                    foundTemplate={foundTemplate}
                    splotTemplates={splotTemplatesProp}
                />

                <FloatingActionBar
                    open={selectionModel.length > 1}
                    selectedCount={selectionModel.length}
                    isPrinting={isPrinting}
                    onExport={() => {/* export logic */ }}
                    onPrint={handleBulkPrint}
                    onDelete={() => {/* delete logic */ }}
                    onClose={closeFloater}
                    type="splot"
                />
            </Grid2>
        )
    );
}

export default memo(SPLotDataGrid);
